import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-relay';
import { MutationParameters, PayloadError } from 'relay-runtime';

import { CreateSettingMutation$data } from '../graphql/__generated__/CreateSettingMutation.graphql';
import { SettingTypeInput } from '../graphql/__generated__/UpdateSettingMutation.graphql';
import { UpdateSettingMutation$data } from '../graphql/__generated__/UpdateSettingMutation.graphql';
import CreateTaskMutation from '../graphql/mutations/CreateTaskMutation';
import { ComponentsDispatch, RootState } from '../store';
import { changeUpdateSettingFetchKey } from '../store/slices/settingSlice';
import { addSpinnerItem, removeSpinnerItem } from '../store/slices/spinnerSlice';
import { ItemTaskType } from '../types';

const useCommitSetting = () => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const [commitCreate, isInFlightCreate] = useMutation(CreateTaskMutation);
    const connectionTaskId = useSelector<RootState, string>((state) => state.calendar.connectionTaskId);

    useEffect(() => {
        if (isInFlightCreate) {
            dispatch(addSpinnerItem('createTask'));
        } else {
            dispatch(removeSpinnerItem('createTask'));
        }
    }, [isInFlightCreate]);

    const getCommitSetting = useMemo(
        () =>
            (
                data: SettingTypeInput,
                connectionId: string,
                id?: string,
                successAdd?: (id: string) => void,
                dontUseOptimistic?: boolean,
            ): MutationParameters => {
                delete (data as any).key;
                delete (data as any).__id;
                delete (data as any).__typename;

                const optimisticUpdateSetting = {
                    updateSetting: {
                        id,
                        ...data,
                    },
                };

                const optimisticUpdater = (store: any) => {
                    if (id) {
                        const oldSetting = store.get(id);
                        if (oldSetting) {
                            oldSetting.setValue(data.data, 'data');
                            oldSetting.setValue(data.name, 'name');
                            oldSetting.setValue(data.is_locked, 'is_locked');
                        }
                    }
                };

                return {
                    variables: {
                        input: {
                            ...data,
                            id,
                        },
                        connections: [connectionId],
                    },
                    optimisticResponse: id && !dontUseOptimistic ? optimisticUpdateSetting : undefined,
                    onCompleted: (
                        response: CreateSettingMutation$data | UpdateSettingMutation$data,
                        errors: PayloadError[],
                    ) => {
                        if (errors) {
                            console.log(
                                (
                                    errors[0] as PayloadError & {
                                        extensions: { debugMessage: string };
                                    }
                                ).extensions.debugMessage,
                                errors,
                            );
                        } else {
                            // console.log('Setting save successful', response, id);

                            if (id) {
                                dispatch(changeUpdateSettingFetchKey());
                            }

                            successAdd?.((response as CreateSettingMutation$data).createSetting?.node?.id!);
                        }
                    },
                    updater: id && !dontUseOptimistic ? optimisticUpdater : undefined,
                    onError: (error: any) => {
                        console.error('Mutation error:', error);
                    },
                } as any as MutationParameters;
            },
        [],
    );

    const addSettingToDataBase = (newSetting: ItemTaskType) => {
        const afterAdding = () => {};

        commitCreate(getCommitSetting(newSetting as SettingTypeInput, connectionTaskId, undefined, afterAdding));
    };

    return { getCommitSetting, addSettingToDataBase };
};

export default useCommitSetting;
